import * as THREE from 'three';

// Cache for camera offset to prevent recalculation on every call
let cachedCameraOffset = null;
let cachedWindowSize = null;

// Clear caches when window resizes to ensure responsive updates
export const clearResponsiveCaches = () => {
  cachedCameraOffset = null;
  cachedWindowSize = null;
  sceneOffsetCache.clear();
};

// Cache for scene offsets to prevent recalculation
const sceneOffsetCache = new Map();

// Camera position offset calculation based on screen size
// This only affects camera position, not FOV
export const getCameraOffset = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  
  // Check if we can use cached offset
  if (cachedCameraOffset !== null && 
      cachedWindowSize && 
      cachedWindowSize.width === width && 
      cachedWindowSize.height === height) {
    return cachedCameraOffset;
  }
  
  // Get breakpoints from sceneConfig
  const breakpoints = sceneConfig.cameraOffsets.breakpoints;
  
  // Find the appropriate offset based on screen size
  // Check breakpoints in order of specificity (mobile -> tablet -> desktop)
  let offset;
  if (width < breakpoints.tablet.minWidth || height < breakpoints.tablet.minHeight) {
    offset = breakpoints.mobile.offset;
  } else if (width < breakpoints.desktop.minWidth || height < breakpoints.desktop.minHeight) {
    offset = breakpoints.tablet.offset;
  } else {
    offset = breakpoints.desktop.offset;
  }
  
  // Cache the result
  cachedCameraOffset = offset;
  cachedWindowSize = { width, height };
  
  return offset;
};

// Apply scene offset - move scene objects away from camera instead of moving camera
export const applySceneOffset = (activeScreen) => {
  const offset = getCameraOffset();
  
  // Create cache key based on activeScreen and offset
  const cacheKey = `${activeScreen}-${offset}`;
  
  // Check if we have a cached result
  if (sceneOffsetCache.has(cacheKey)) {
    return sceneOffsetCache.get(cacheKey);
  }
  
  // Get the base scene object position from sceneConfig
  let basePosition = [0, 0, 0]; // Default fallback
  
  if (activeScreen === 'logo' || activeScreen === null) {
    // Logos are positioned at Z=-25 (this is not in modelPositions)
    // null activeScreen also means we're showing logos
    basePosition = [0, 0, -25];
  } else if (sceneConfig.modelPositions[activeScreen]) {
    // Use the position from modelPositions
    basePosition = sceneConfig.modelPositions[activeScreen].position;
  }
  
  // Calculate direction from camera (at origin) to scene
  const dx = basePosition[0] - 0; // Camera is at origin
  const dz = basePosition[2] - 0; // Camera is at origin
  
  // Normalize the direction vector
  const distance = Math.sqrt(dx * dx + dz * dz);
  const dirX = dx / distance;
  const dirZ = dz / distance;
  
  // Apply offset to move scene objects away from camera
  const result = [
    basePosition[0] + dirX * offset,
    basePosition[1],
    basePosition[2] + dirZ * offset
  ];
  
  // Cache the result
  sceneOffsetCache.set(cacheKey, result);
  
  return result;
};

// Legacy function - kept for backward compatibility but now just returns original position
export const applyCameraOffset = (targetPosition, activeScreen) => {
  // Camera no longer moves - scenes move instead
  return targetPosition;
};

// Calculate phone handset world position from phonebox scene
export const getPhonePosition = (phoneboxScene) => {
  if (!phoneboxScene) {
    return null;
  }

  // Find the phone mesh in the scene
  let phoneMesh = null;
  phoneboxScene.traverse((child) => {
    if (child.isMesh && child.name === 'phone') {
      phoneMesh = child;
    }
  });

  if (!phoneMesh) {
    return null;
  }

  // Get the phone's bounding box to find the top
  const boundingBox = new THREE.Box3().setFromObject(phoneMesh);
  const phoneWorldPosition = new THREE.Vector3();
  phoneMesh.getWorldPosition(phoneWorldPosition);
  
  // Calculate the top of the phone by adding half the height to the Y position
  const phoneHeight = boundingBox.max.y - boundingBox.min.y;
  const topY = phoneWorldPosition.y + (phoneHeight / 2);
  
  return [phoneWorldPosition.x, topY, phoneWorldPosition.z];
};

// Calculate TV screen world position from TV scene
export const getTvPosition = (tvScene) => {
  if (!tvScene) {
    return null;
  }

  // Find the screen-display mesh in the scene
  let screenMesh = null;
  tvScene.traverse((child) => {
    if (child.isMesh && child.name.toLowerCase() === 'screen-display') {
      screenMesh = child;
    }
  });

  if (!screenMesh) {
    // Fallback to scene world position if screen mesh not found
    const sceneWorldPosition = new THREE.Vector3();
    tvScene.getWorldPosition(sceneWorldPosition);
    return [sceneWorldPosition.x, sceneWorldPosition.y, sceneWorldPosition.z];
  }

  // Get the screen's world position directly (same approach as phone, but without height offset)
  // The verticalOffset in sceneConfig will handle the vertical positioning
  const screenWorldPosition = new THREE.Vector3();
  screenMesh.getWorldPosition(screenWorldPosition);
  
  return [screenWorldPosition.x, screenWorldPosition.y, screenWorldPosition.z];
};

// Calculate SoundWaves position based on phone handset position and scene offset
export const getSoundWavesPosition = (cameraRef, phonePosition) => {
  if (!cameraRef?.current || !phonePosition) {
    return { x: 140, y: 'calc(50% - 50px)' }; // Fallback to original position
  }

  // Get the soundWaves configuration
  const soundWavesConfig = sceneConfig.soundWaves;
  
  // Calculate the 3D world position where sound waves should appear
  // This is the phone position + the base offset
  const worldPosition = [
    phonePosition[0] + soundWavesConfig.basePosition.x,
    phonePosition[1] + soundWavesConfig.basePosition.y,
    phonePosition[2] + soundWavesConfig.basePosition.z
  ];

  // Update camera aspect ratio to match current viewport size
  // This is critical for responsive positioning on smaller screens
  const camera = cameraRef.current;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const aspect = width / height;
  
  // Update camera aspect ratio if it has changed
  if (camera.aspect !== aspect) {
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
  }

  // Convert 3D world position to 2D screen coordinates
  const vector = new THREE.Vector3(...worldPosition);
  vector.project(camera);

  // Convert normalized device coordinates to screen pixels
  const x = (vector.x * 0.5 + 0.5) * width;
  const y = (vector.y * -0.5 + 0.5) * height;

  // Get responsive breakpoint configuration - match camera offset breakpoints exactly
  const breakpoints = soundWavesConfig.responsive.breakpoints;
  const cameraBreakpoints = sceneConfig.cameraOffsets.breakpoints;
  let responsiveConfig;
  
  // Use same logic as camera offsets: check tablet threshold first, then desktop
  // Mobile: width < 768 OR height < 600
  // Tablet: width >= 768 AND height >= 600, but width < 1200 OR height < 800
  // Desktop: width >= 1200 AND height >= 800
  if (width < cameraBreakpoints.tablet.minWidth || height < cameraBreakpoints.tablet.minHeight) {
    responsiveConfig = breakpoints.mobile;
  } else if (width < cameraBreakpoints.desktop.minWidth || height < cameraBreakpoints.desktop.minHeight) {
    responsiveConfig = breakpoints.tablet;
  } else {
    responsiveConfig = breakpoints.desktop;
  }

  // Apply responsive screen offset (overrides default if specified)
  const screenOffsetX = responsiveConfig.screenOffset?.x ?? soundWavesConfig.screenOffset.x;
  const screenOffsetY = responsiveConfig.screenOffset?.y ?? soundWavesConfig.screenOffset.y;
  
  const finalX = x + screenOffsetX;
  const finalY = y + screenOffsetY;

  // Calculate responsive scale
  const scale = responsiveConfig.scale ?? 1.0;
  // Use base dimensions for clamping since we're using CSS transform scale
  const baseWidth = 250;
  const baseHeight = 150;

  return {
    x: Math.max(0, Math.min(width - baseWidth, finalX)), // Clamp to screen bounds
    y: Math.max(0, Math.min(height - baseHeight, finalY)), // Clamp to screen bounds
    scale: scale // Return scale for use in component (applied via CSS transform)
  };
};

// Scene configuration settings
export const sceneConfig = {
  // Floor settings
  showFloor: false,
  
  // Shadow settings - will be overridden by GPU tier detection
  showShadows: false,
  
  // Lighting settings - will be overridden by GPU tier detection
  lighting: {
    ambientIntensity: 0.5,
    directionalIntensity: 3.5,
    directionalPosition: [5, 15, 5],
    shadowMapSize: 2048, // Will be overridden by GPU tier
    shadowCamera: {
      far: 50,
      left: -40,
      right: 10,
      top: 20,
      bottom: -20
    }
  },
  
  // Floor settings
  floor: {
    position: [0, -12, 0],
    size: [200, 200],
    color: "#F20"
  },
  
  // Camera offset settings for responsive behavior
  cameraOffsets: {
    // Screen size breakpoints and their corresponding camera offsets
    breakpoints: {
      desktop: { minWidth: 1200, minHeight: 800, offset: 0 },
      tablet: { minWidth: 768, minHeight: 600, offset: 5 },
      mobile: { minWidth: 0, minHeight: 0, offset: 20 }
    }
  },
  
  // Camera states for different scenes
  cameraStates: {
    logo: {
      position: [0, 0, 0], // At origin to properly frame the logos at -25
      rotation: [0, 0, 0],
      fov: 60
    },
    phonebox: {
      position: [0, 0, 0], // At origin, centered for phonebox view
      rotation: [0, Math.PI / 2, 0], // 90 degrees to face phonebox
      fov: 60
    },
    phoneboxOffHook: {
      position: [-18, 8, -0.37], // Close-up view when phone is off the hook
      rotation: [0, Math.PI / 2, 0], // Same rotation as normal phonebox view
      fov: 60
    },
    tv: {
      position: [0, 0, 0], // At origin, centered for TV view
      rotation: [0, -Math.PI / 2, 0], // -90 degrees to face TV (right side)
      fov: 60
    },
    tvZoomed: {
      position: [45, 5, -4.5], // Close-up view when TV is clicked
      rotation: [0, -Math.PI / 2, 0], // Same rotation as normal TV view
      fov: 60
    },
    news: {
      position: [0, 0, 0], // At origin, looking straight ahead
      rotation: [0, Math.PI, 0], // 180 degrees to face news stand (back)
      fov: 60 // Standard field of view like other cameras
    }
  },

  
  // Model positions in the scene - restore original phonebox position, add new models
  modelPositions: {
    phonebox: {
      position: [-30, -14, 0], // Further down on Y axis
      rotation: [0, Math.PI / 2, 0], // Original phonebox rotation
      scale: 2.0
    },
    tv: {
      position: [100, -40, 0], // Right side of phonebox, same level
      rotation: [0, -Math.PI, 0], // Face towards center
      scale: 80 // Increased scale for better visibility
    },
    news: {
      position: [0, -60, 140], // Closer to camera and higher up for better visibility
      rotation: [0, -Math.PI, 0], // Face towards center
      scale: 1 // Reduced scale - news stands are typically smaller than TVs
    }
  },

  // PhoneCable configuration
  phoneCable: {
    // Anchor point positions relative to their parent meshes
    anchorPoints: {
      phone: [0, 0.01, -0.09], // Relative to phonebox mesh (base)
      phonebox: [-0.6, 1.6, -11]  // Relative to phone mesh (handset/receiver)
    },
    // Cable appearance and physics
    cable: {
      segments: 24, // Number of segments for smooth curve
      thickness: 0.05, // Much thicker cable for visibility at distance
      color: 0x666666, // Lighter gray color for better visibility
      sag: 3, // Base sag amount (will be scaled logarithmically with distance)
      fixedLength: 2.5, // Fixed cable length - always long enough to reach anchor points
      material: {
        roughness: 0.8,
        metalness: 0.1
      }
    }
  },

  // CameraAlignedLabels configuration
  alignedLabels: {
    // Default settings for labels
    default: {
      depthOffset: 10,
      horizontalOffset: 6.2,
      verticalOffset: -1,
      fadeInDelay: 1000,
      fadeOutDelay: 500
    },
    // Per-scene label settings (only include values that differ from defaults)
    scenes: {
      phonebox: {
        fadeInDelay: 500
      },
      tv: {
        depthOffset: 85,
        horizontalOffset: 12,
        verticalOffset: 10,
        fadeInDelay: 500, // Match phonebox fade-in delay
      },
    },
    // Responsive breakpoints for label adjustments
    responsive: {
      breakpoints: {
        desktop: { 
          minWidth: 1200, 
          minHeight: 800, 
          depthMultiplier: 1.0, 
          horizontalMultiplier: 1.0,
          verticalMultiplier: 1.0
        },
        tablet: { 
          minWidth: 768, 
          minHeight: 600, 
          depthMultiplier: 1.2, 
          horizontalMultiplier: 1.0,
          verticalMultiplier: 1.0
        },
        mobile: { 
          minWidth: 0, 
          minHeight: 0, 
          depthMultiplier: 1.5, 
          horizontalMultiplier: 1.0,
          verticalMultiplier: 1.0
        }
      }
    }
  },

  // SoundWaves configuration
  soundWaves: {
    // Default settings
    depthOffset: -1,      // Distance toward camera from phone position
    horizontalOffset: -0.6,    // Leftward offset in world units (negative = left)
    verticalOffset: 1.35,      // Vertical offset in world units
    scale: 0.4,            // Fixed scale for all screen sizes
    // Responsive breakpoints for offsets and scale adjustments
    responsive: {
      breakpoints: {
        desktop: { 
          minWidth: 1200, 
          minHeight: 800,          // Use default if not specified
        },
        tablet: { 
          minWidth: 768, 
          minHeight: 600,
          horizontalOffset: 0.12,
          verticalOffset: 1.1,
        },
        mobile: { 
          minWidth: 0, 
          minHeight: 0,
          horizontalOffset: 0.5,
          verticalOffset: 1,
        }
      }
    }
  }
};


// Create performance-optimized scene configuration based on GPU tier
export const createOptimizedSceneConfig = (performanceSettings) => {
  return {
    ...sceneConfig,
    showShadows: performanceSettings.enableShadows,
    lighting: {
      ...sceneConfig.lighting,
      shadowMapSize: performanceSettings.shadowMapSize
    },
    performance: {
      maxFPS: performanceSettings.maxFPS,
      textureQuality: performanceSettings.textureQuality,
      animationQuality: performanceSettings.animationQuality,
      staticFPS: performanceSettings.staticFPS,
      dpr: performanceSettings.dpr || [1, 2]
    }
  };
};

export default sceneConfig;
